"use client";
/* eslint-disable @next/next/no-img-element */
import Link from "next/link";
import { useState } from "react";
import {
  ArrowUpRight,
  ArrowRight,
  Scale,
  Menu,
  X,
  FolderOpen,
  BriefcaseBusiness,
  BookOpen,
  CalendarDays,
  LockKeyhole,
  Layers,
  Check,
  Plus,
  Minus,
  ShieldCheck,
  Search,
} from "lucide-react";
import { brand } from "@/lib/brand";
export function Mark() {
  return (
    <Link href="/" className="dc-mark">
      <span className="dc-emblem">
        <Scale size={22} strokeWidth={1.3} />
      </span>
      <span>
        {brand.name}
        <small>BY {brand.company.toUpperCase()}</small>
      </span>
    </Link>
  );
}
const links = [
  ["Platform", "/platform"],
  ["How it works", "/how-it-works"],
  ["Trust & privacy", "/security"],
  ["Advocates", "/advocates"],
  ["Membership", "/plans"],
];
export function Header() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <header className="dc-header">
        <Mark />
        <nav aria-label="Primary navigation">
          {links.map(([t, p]) => (
            <Link href={p} key={p}>
              {t}
            </Link>
          ))}
        </nav>
        <div className="dc-header-actions">
          <Link className="dc-signin" href="/sign-in">
            Sign in <ArrowUpRight size={14} />
          </Link>
          <button
            className="dc-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
      {open && (
        <nav className="dc-mobile-nav" aria-label="Mobile navigation">
          {links.map(([t, p]) => (
            <Link href={p} key={p} onClick={() => setOpen(false)}>
              {t}
              <ArrowUpRight size={16} />
            </Link>
          ))}
        </nav>
      )}
    </>
  );
}
export function Footer() {
  return (
    <footer className="dc-footer">
      <div>
        <Mark />
        <p>
          A considered place for
          <br />
          the work that matters.
        </p>
      </div>
      <div>
        <small>THE PLATFORM</small>
        {links.slice(0, 3).map(([t, p]) => (
          <Link key={p} href={p}>
            {t}
          </Link>
        ))}
      </div>
      <div>
        <small>YOUR PRESENCE</small>
        <Link href="/advocates">Advocate directory</Link>
        <Link href="/plans">Membership</Link>
        <Link href="/about">About the company</Link>
      </div>
      <div>
        <small>YOUR WORKSPACE</small>
        <Link href="/sign-in">Chamber sign in</Link>
        <Link href="/sign-in?office=1">Administration office</Link>
        <Link href="/how-it-works#pilot">Pilot onboarding</Link>
      </div>
      <div className="dc-footer-bottom">
        <span>© 2026 Chatake Innoworks Private Limited</span>
        <span>DESIGNED FOR THE PRACTICE OF LAW</span>
        <span>Local product preview</span>
      </div>
    </footer>
  );
}
export function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="dc-public">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
    </div>
  );
}
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="dc-eyebrow">
      <span />
      {children}
    </p>
  );
}
export function Action({
  href,
  children,
  light = false,
}: {
  href: string;
  children: React.ReactNode;
  light?: boolean;
}) {
  return (
    <Link className={`dc-action ${light ? "light" : ""}`} href={href}>
      {children}
      <ArrowUpRight size={17} />
    </Link>
  );
}
const features = [
  {
    icon: BriefcaseBusiness,
    num: "01",
    title: "A home for every matter.",
    text: "Clients, documents, dates and preparation. Together in a case file that follows your train of thought.",
    to: "/platform#matters",
  },
  {
    icon: FolderOpen,
    num: "02",
    title: "The record, within reach.",
    text: "Give every document its place. Search, read and return to the original without losing the context.",
    to: "/platform#documents",
  },
  {
    icon: BookOpen,
    num: "03",
    title: "Room for deeper thinking.",
    text: "Bring notes, source material and structured arguments to a research desk of your own.",
    to: "/platform#research",
  },
];
export function PublicHome() {
  return (
    <PublicShell>
      <section className="dc-hero">
        <div className="dc-hero-copy">
          <Eyebrow>THE MODERN ADVOCATE’S WORKSPACE</Eyebrow>
          <h1>
            Your practice.
            <br />
            Perfectly <em>in order.</em>
          </h1>
          <p className="dc-hero-description">
            A quieter, more considered way to manage the work of law. Your
            matters, documents and preparation — in one personal digital
            chamber.
          </p>
          <div className="dc-hero-actions">
            <Action href="/sign-in">Explore your chamber</Action>
            <Link className="dc-text-link" href="/how-it-works">
              Discover the experience <ArrowRight size={16} />
            </Link>
          </div>
          <div className="dc-hero-foot">
            <span className="dc-tiny-seal">
              <LockKeyhole size={14} />
            </span>
            <span>
              YOUR WORK. YOUR CHAMBER.
              <br />
              <b>One place to return to.</b>
            </span>
            <span className="dc-edition">
              THE FOUNDING EDITION
              <br />
              INDIA · MMXXVI
            </span>
          </div>
        </div>
        <div className="dc-hero-visual">
          <img
            src="/chamber-hero.png"
            alt="Sunlit legal chamber with case papers and a tablet"
            fetchPriority="high"
          />
          <div className="dc-image-caption">
            <span>THE CHAMBER, REIMAGINED</span>
            <span>01 / 03</span>
          </div>
          <div className="dc-floating-file">
            <div className="dc-file-icon">
              <FolderOpen size={23} strokeWidth={1.3} />
            </div>
            <div>
              <small>YOUR NEXT MATTER</small>
              <strong>Prepared. Wherever you are.</strong>
              <span>From the desk to the courtroom.</span>
            </div>
            <ArrowUpRight size={19} />
          </div>
        </div>
      </section>
      <div className="dc-principles">
        <span>BUILT AROUND YOUR PRACTICE</span>
        <p>
          <BriefcaseBusiness />
          Matter by matter
        </p>
        <p>
          <LockKeyhole />
          Private by design
        </p>
        <p>
          <Layers />
          Across your devices
        </p>
        <p>
          <Scale />
          Advocate in control
        </p>
      </div>
      <section className="dc-section dc-intro">
        <div>
          <Eyebrow>A PLACE FOR EVERYTHING</Eyebrow>
          <h2>
            Less looking for files.
            <br />
            More <em>clarity of thought.</em>
          </h2>
        </div>
        <p>
          A chamber is more than a place to store documents. It is where the
          facts come together, arguments take shape and the next step becomes
          clear.
        </p>
        <div className="dc-features">
          {features.map((f) => (
            <Link key={f.num} className="dc-feature" href={f.to}>
              <div>
                <f.icon strokeWidth={1.2} />
                <small>{f.num}</small>
              </div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
              <span>
                Explore the workspace <ArrowUpRight size={16} />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <section className="dc-workday">
        <div className="dc-workday-copy">
          <Eyebrow>FROM FIRST BRIEF TO FINAL NOTE</Eyebrow>
          <h2>
            A familiar rhythm.
            <br />
            <em>A finer workspace.</em>
          </h2>
          <p>
            Open your chamber to a clear view of the day. Move into a matter,
            read the record, make a note. Your work stays connected as you move.
          </p>
          <Action href="/sign-in" light>
            Take a look inside
          </Action>
          <div className="dc-device-list">
            DESKTOP <span>·</span> IPAD <span>·</span> MOBILE
          </div>
        </div>
        <div className="dc-preview">
          <div className="dc-preview-top">
            <Scale size={17} />
            <span>MAHESH JAGTAP’S CHAMBER</span>
            <span className="dc-dot" />
          </div>
          <div className="dc-preview-body">
            <small>YOUR DAILY BRIEFING</small>
            <h3>
              A little more prepared.
              <br />A little more at ease.
            </h3>
            <div className="dc-preview-metrics">
              <span>
                <b>02</b>Demo matters
              </span>
              <span>
                <b>02</b>Next actions
              </span>
              <span>
                <b>5 GB</b>Chamber allowance
              </span>
            </div>
            <div className="dc-preview-row">
              <CalendarDays />
              <span>
                <b>Prepare the hearing brief</b>
                <small>Aster Trading · fictional sample</small>
              </span>
              <ArrowUpRight size={16} />
            </div>
            <div className="dc-preview-row">
              <FolderOpen />
              <span>
                <b>Your document library</b>
                <small>A place for your first upload</small>
              </span>
              <ArrowUpRight size={16} />
            </div>
            <Link href="/sign-in">
              Open the interactive preview <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
      <section className="dc-section dc-presence">
        <div className="dc-profile-art">
          <div className="dc-portrait-monogram">
            MJ<span>ADVOCATE</span>
          </div>
          <div>
            <small>PROFESSIONAL PROFILE · PREVIEW</small>
            <h3>Mahesh Jagtap</h3>
            <div className="dc-hairline" />
            <p>
              A professional identity.
              <br />
              Thoughtfully presented.
            </p>
            <Link href="/advocates/mahesh-jagtap">
              View profile concept <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
        <div>
          <Eyebrow>PRIVATE WORK. PUBLIC PRESENCE.</Eyebrow>
          <h2>
            Known for your work.
            <br />
            <em>On your terms.</em>
          </h2>
          <p>
            A restrained professional profile, separate from your private
            chamber. You decide which verified details become public. A personal
            domain can follow when you need one.
          </p>
          <Action href="/advocates">Explore professional profiles</Action>
        </div>
      </section>
      <section className="dc-closing">
        <Eyebrow>YOUR NEXT CHAPTER</Eyebrow>
        <h2>
          Make room for
          <br />
          <em>your finest work.</em>
        </h2>
        <Action href="/sign-in">Step inside Digital Chambers</Action>
        <p>For independent advocates. For growing chambers.</p>
      </section>
    </PublicShell>
  );
}
const faqs = [
  [
    "Where does my 5 GB come from?",
    "It is a chamber allowance managed by the platform. Review builds use development storage. The production Google Drive document connection is not yet active.",
  ],
  [
    "Where will chamber documents be stored?",
    "The selected plan uses our company-controlled Google Drive pool with a 5 GB allowance per chamber, managed through Digital Chambers. The production connection remains pending, and review builds contain sample files only.",
  ],
  [
    "Is this ready for confidential customer files?",
    "This is a protected product review with fictional sample records. Drive storage, malware scanning, backups and final production acceptance must be completed before real customer onboarding.",
  ],
  [
    "Do I need a separate website?",
    "A professional profile within Digital Chambers is the starting point. A custom domain or separate personal website can be an optional service.",
  ],
  [
    "Does the assistant give legal advice?",
    "The current research desk finds passages in uploaded text files. Generative AI is not connected. Future analysis will be assistive, source-aware and subject to advocate verification.",
  ],
];
export function InfoPage({ page }: { page: string }) {
  const [faq, setFaq] = useState(-1);
  const content: Record<
    string,
    { label: string; title: string; italic: string; description: string }
  > = {
    platform: {
      label: "THE DIGITAL CHAMBER",
      title: "The whole matter.",
      italic: "A single workspace.",
      description:
        "Bring the moving parts of a legal practice into a thoughtful, connected working environment.",
    },
    security: {
      label: "TRUST & PRIVACY",
      title: "Confidentiality begins",
      italic: "with clear boundaries.",
      description:
        "Your public presence, private work and platform operations have different responsibilities. We keep their boundaries explicit.",
    },
    plans: {
      label: "MEMBERSHIP",
      title: "A place to begin.",
      italic: "Room to grow.",
      description:
        "A considered starting point for independent advocates, with space for a growing practice.",
    },
    "how-it-works": {
      label: "THE CHAMBER EXPERIENCE",
      title: "Your work,",
      italic: "with you.",
      description:
        "A simple journey from the first document to an organised, matter-centred practice.",
    },
    about: {
      label: "BY CHATAKE INNOWORKS",
      title: "Thoughtful technology.",
      italic: "Purposeful work.",
      description:
        "Digital Chambers is a professional workspace being developed by Chatake Innoworks Private Limited, with a first pilot for Adv. Mahesh Jagtap.",
    },
  };
  const c = content[page];
  return (
    <PublicShell>
      <section className="dc-page-hero">
        <Eyebrow>{c.label}</Eyebrow>
        <h1>
          {c.title}
          <br />
          <em>{c.italic}</em>
        </h1>
        <p>{c.description}</p>
      </section>
      {page === "platform" ? (
        <section className="dc-section dc-product-list">
          {features.map((f, i) => (
            <article id={["matters", "documents", "research"][i]} key={f.num}>
              <span className="dc-big-number">{f.num}</span>
              <div>
                <f.icon />
                <h2>{f.title}</h2>
                <p>{f.text}</p>
                <ul>
                  {[
                    [
                      "Create matters and client records",
                      "Add dates, notes and structured arguments",
                      "Track tasks and matter activity",
                    ],
                    [
                      "Upload, preview and download files",
                      "Organise nested folders and matter associations",
                      "Search, rename, trash and restore",
                    ],
                    [
                      "Search extracted TXT sources",
                      "Save research questions and notes",
                      "Build arguments with supporting facts",
                    ],
                  ][i].map((s) => (
                    <li key={s}>
                      <Check size={15} />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              <Action href="/sign-in">Try this workspace</Action>
            </article>
          ))}
        </section>
      ) : page === "plans" ? (
        <section className="dc-section">
          <div className="dc-plan-grid">
            {[
              {
                name: "Founding pilot",
                price: "By invitation",
                desc: "A personal chamber to begin with.",
                space: "5 GB",
                items: [
                  "One advocate",
                  "Matters, files and tasks",
                  "Professional profile draft",
                ],
              },
              {
                name: "Professional",
                price: "Coming next",
                desc: "For an established independent practice.",
                space: "25 GB proposed",
                items: [
                  "Expanded document library",
                  "Research and preparation tools",
                  "Reviewed public profile",
                ],
              },
              {
                name: "Chamber",
                price: "Let’s shape it",
                desc: "For a practice that works together.",
                space: "Custom allowance",
                items: [
                  "Assigned-matter team roles",
                  "Onboarding and migration",
                  "Optional personal domain",
                ],
              },
            ].map((p, i) => (
              <article className={i === 0 ? "featured" : ""} key={p.name}>
                <small>0{i + 1} / MEMBERSHIP</small>
                <h3>{p.name}</h3>
                <p>{p.desc}</p>
                <h4>{p.price}</h4>
                <span className="dc-plan-space">
                  <FolderOpen size={16} />
                  {p.space}
                </span>
                <ul>
                  {p.items.map((s) => (
                    <li key={s}>
                      <Check size={14} />
                      {s}
                    </li>
                  ))}
                </ul>
                <Action href="/sign-in">Explore this experience</Action>
              </article>
            ))}
          </div>
          <p className="dc-note">
            Plan concepts for review. No payment, subscription or cloud storage
            allocation is activated.
          </p>
        </section>
      ) : page === "security" ? (
        <section className="dc-section dc-trust-grid">
          {[
            [
              "01",
              "Chamber access",
              "The local API checks membership before reading or changing records. Platform administration does not grant access to customer files.",
            ],
            [
              "02",
              "Deliberate publication",
              "Private records never feed directly into the public directory. Mahesh’s page is a labelled concept pending verification.",
            ],
            [
              "03",
              "Files under your control",
              "Uploads are measured against a chamber quota. Downloads require a session, and deleted files remain recoverable in trash.",
            ],
            [
              "04",
              "Honest intelligence",
              "Source excerpts are distinguished from AI analysis. No private document is sent to an unconfigured AI service.",
            ],
          ].map(([n, t, d]) => (
            <article key={n}>
              <small>{n}</small>
              <ShieldCheck />
              <h3>{t}</h3>
              <p>{d}</p>
            </article>
          ))}
          <p className="dc-note">
            Local review status: managed production sign-in/MFA, malware
            scanning, cloud backups and provider connections remain to be
            configured. Local disk protection depends on the Mac’s encryption
            settings.
          </p>
        </section>
      ) : (
        <section className="dc-section dc-steps" id="pilot">
          {[
            [
              "01",
              "Enter your chamber",
              "Use the clearly labelled local pilot session. Mahesh’s identity stays separate from the platform administrator.",
            ],
            [
              "02",
              "Give the matter a home",
              "Create a matter and client, add a hearing date and write your first preparation note.",
            ],
            [
              "03",
              "Bring the record together",
              "Upload a sample document, associate it with a matter and read it from the library.",
            ],
            [
              "04",
              "Return to a clear next step",
              "Track tasks, collect research and shape arguments. Your local work persists when you refresh.",
            ],
          ].map(([n, t, d]) => (
            <article key={n}>
              <span>{n}</span>
              <div>
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
              <ArrowUpRight />
            </article>
          ))}
        </section>
      )}
      <section className="dc-section dc-faq">
        <Eyebrow>A FEW USEFUL ANSWERS</Eyebrow>
        <h2>
          Before you <em>step inside.</em>
        </h2>
        {faqs.map(([q, a], i) => (
          <article key={q}>
            <button
              onClick={() => setFaq(faq === i ? -1 : i)}
              aria-expanded={faq === i}
            >
              {q}
              {faq === i ? <Minus size={18} /> : <Plus size={18} />}
            </button>
            {faq === i && <p>{a}</p>}
          </article>
        ))}
      </section>
    </PublicShell>
  );
}
export function AdvocateDirectory() {
  const [q, setQ] = useState("");
  const match = "mahesh jagtap".includes(q.toLowerCase());
  return (
    <PublicShell>
      <section className="dc-page-hero">
        <Eyebrow>PROFESSIONAL PRESENCE</Eyebrow>
        <h1>
          A name. A practice.
          <br />
          <em>A considered introduction.</em>
        </h1>
        <p>
          The directory will contain advocate-approved professional information.
          Private chamber work stays private.
        </p>
      </section>
      <section className="dc-section dc-directory">
        <label className="dc-search">
          <Search size={19} />
          <input
            aria-label="Search advocates"
            placeholder="Search an advocate by name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        {match ? (
          <Link href="/advocates/mahesh-jagtap" className="dc-directory-row">
            <span className="dc-avatar large">MJ</span>
            <div>
              <small>FOUNDING PILOT · PROFILE CONCEPT</small>
              <h3>Adv. Mahesh Jagtap</h3>
              <p>Professional details awaiting verification</p>
            </div>
            <ArrowUpRight />
          </Link>
        ) : (
          <p className="dc-empty">No profiles match “{q}”. Try another name.</p>
        )}
        <p className="dc-note">
          No enrolment, practice area or experience claims have been verified
          for this concept.
        </p>
      </section>
    </PublicShell>
  );
}
export function MaheshProfile() {
  return (
    <PublicShell>
      <section className="dc-page-hero dc-profile-hero">
        <span className="dc-avatar large">MJ</span>
        <Eyebrow>ADVOCATE · FOUNDING PILOT</Eyebrow>
        <h1>
          Mahesh <em>Jagtap.</em>
        </h1>
        <p>
          A professional profile concept. Publication awaits advocate approval
          and verification.
        </p>
      </section>
      <section className="dc-section dc-profile-details">
        <div>
          <Eyebrow>PROFESSIONAL INTRODUCTION</Eyebrow>
          <h2>
            A space for
            <br />
            <em>your professional story.</em>
          </h2>
          <p>
            Mahesh’s verified biography, practice areas and jurisdictions will
            appear here after he reviews and approves them. No experience,
            achievements or professional credentials have been invented.
          </p>
        </div>
        <aside>
          <small>PUBLICATION CHECKLIST</small>
          {[
            "Professional biography",
            "Bar enrolment",
            "Practice areas & jurisdiction",
            "Public contact preference",
          ].map((t) => (
            <p key={t}>
              {t}
              <span>Pending</span>
            </p>
          ))}
          <Link href="/sign-in">
            Complete your private profile <ArrowUpRight size={16} />
          </Link>
        </aside>
      </section>
    </PublicShell>
  );
}
